import React from 'react';
import InfoTip from '../InfoTip';
import { buildGagOwnerOptions, gagOwnerPickPatch } from './gagOwnerOptions';

// Shared gag-form field styling — single source for the gag editor
// (GagDetailPanel), the create modal (GagCreateModal) and this picker.
export const FIELD_CLS =
  'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 ' +
  'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 ' +
  'focus:ring-2 focus:ring-blue-500 focus:outline-none';
export const LABEL_CLS = 'block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1';

/**
 * Linked Sub-department ↔ Owner picker (Round 3) — THE single implementation
 * of the pairing rules, shared by the gag editor (GagDetailPanel) and the
 * create modal (GagCreateModal). Renders TWO sibling grid cells (a fragment):
 * the Sub-department select and the Owner ("Assigned to") select.
 *
 * 🔀 2026-10-18: the OPTION LIST and rule 5's patch live in
 * `./gagOwnerOptions.js` (`buildGagOwnerOptions`, `gagOwnerPickPatch`), shared
 * with the Breakdown row's Owner cell (`GagRowEditCell.js`). This component
 * is the two-select RENDERING; the rules below are what that module computes.
 *
 * Linkage rules (Pete's Round-3 spec):
 *  1. No sub-department chosen → ALL department users are offered.
 *  2. Sub-department chosen → only users assigned to THAT sub-department
 *     (via department_assignments.sub_department_id, carried on
 *     /api/gags/assignable-users rows).
 *  3. STRICT rule (documented edge decision): users with NO sub-department
 *     drop out whenever a sub-dept filter is active — except the keep-listed
 *     entries below.
 *  4. Keep-listed (documented edge decision): the gag's persisted owner and
 *     the current selection are ALWAYS listed — suffixed "(other sub-dept)"
 *     when they mismatch the active filter — and a mismatched selection is
 *     kept, never silently cleared.
 *  5. User-first flow: picking an owner fills a BLANK sub-department from the
 *     owner's own sub-department (fill-when-blank — mirrors the server's
 *     autoFillSubDeptFromOwner; a user with no sub-dept changes nothing, and
 *     a non-blank sub-dept is never overwritten).
 *
 * Permission modes (mirrors the server's PUT owner_id / POST owner_id rules):
 *  - canManage: the full assignable-users list under rules 1–4.
 *  - non-manager: only Unassigned + "Me" (self-claim/take semantics, offered
 *    regardless of sub-department, merely suffixed on mismatch) plus the
 *    keep-listed current owner. 🔀 2026-10-02: `selfClaimAllowed={false}`
 *    drops "Me" — the gag EDITOR passes it for a non-manager, because
 *    `PUT /api/gags/:id` owner_id=self now needs breakdown rights (owner:
 *    "No, managers assign"). The CREATE modal keeps "Me" (create-with-self-
 *    owner is still allowed, pending an owner ruling).
 *  - A manager missing from the dept list (global admins have no
 *    department_assignments row) gets the same always-offered "Me" entry.
 *
 * All values are STRINGS ('' = none/unassigned) — the callers' form-snapshot
 * diffing relies on the all-strings invariant; conversion to int-or-null
 * happens at the API boundary in the callers.
 *
 * Props:
 *  - subDepts: [{ id, name }]
 *  - users: assignable users [{ id, display_name, sub_department_id, ... }]
 *  - canManage: manager rights in this department
 *  - userId: current user id (int; may be NaN)
 *  - subDeptValue / ownerValue: current form values (strings)
 *  - currentOwner: { id, name } | null — the gag's PERSISTED owner (editor
 *    only; null in the create modal)
 *  - onPatch(patch): apply { sub_department_id?, owner_id? } to the form
 *  - disabled, ownerLabel ('Assigned to' default), idSuffix (unique DOM aid)
 */
export default function OwnerSubDeptFields({
  subDepts,
  users,
  canManage,
  userId,
  subDeptValue,
  ownerValue,
  currentOwner = null,
  onPatch,
  disabled = false,
  ownerLabel = 'Assigned to',
  // Optional ⓘ text per caption (GagDetailPanel); absent = the caption renders as before.
  subDeptTip = null,
  ownerTip = null,
  selfClaimAllowed = true,
}) {
  const options = buildGagOwnerOptions({
    users, canManage, userId, subDeptValue, ownerValue, currentOwner, selfClaimAllowed,
  });

  const handleSubDeptChange = (e) => onPatch({ sub_department_id: e.target.value });

  // Rule 5 — fill-when-blank, owner picked first (gagOwnerOptions.js).
  const handleOwnerChange = (e) =>
    onPatch(gagOwnerPickPatch({ ownerValue: e.target.value, subDeptValue, users }));

  return (
    <>
      <div>
        <label className={LABEL_CLS}>{subDeptTip ? <InfoTip title={subDeptTip} label="Sub-department" /> : 'Sub-department'}</label>
        <select
          value={subDeptValue}
          onChange={handleSubDeptChange}
          disabled={disabled}
          aria-label="Sub-department"
          className={FIELD_CLS}
        >
          <option value="">None</option>
          {subDepts.map((sd) => (
            <option key={sd.id} value={sd.id}>{sd.name}</option>
          ))}
        </select>
      </div>
      <div>
        <label className={LABEL_CLS}>{ownerTip ? <InfoTip title={ownerTip} label={ownerLabel} /> : ownerLabel}</label>
        <select
          value={ownerValue}
          onChange={handleOwnerChange}
          disabled={disabled}
          aria-label={ownerLabel}
          className={FIELD_CLS}
        >
          <option value="">Unassigned</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>
    </>
  );
}
